import React, { CSSProperties, useState } from "react";
import styled from "styled-components";
import {
	ColorV2,
	Avatar,
	Text,
	CellSelector,
	Filter,
} from "@adoptaunabuelo/react-components";

const AvatarContainer = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
`;

const StyledTable = styled.table`
	width: 100%;
	table-layout: fixed;
	border-collapse: separate;
	min-height: 400px;
`;

const StyledTdAvatar = styled.td`
	width: 40px;
	text-align: left;
	border-bottom: none;
`;

const StyledTbody = styled.tbody`
	display: table;
	width: 100%;
	table-layout: fixed;
`;

const HeaderCell = styled.div<{ $isAge?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: flex-start;

	@media (max-width: 768px) {
		justify-content: ${({ $isAge }) =>
			$isAge ? "flex-end" : "flex-start"};
	}
`;

const ScrollableTr = styled.tr`
	padding: 4px 16px;
	display: table;
	width: -webkit-fill-available;
	width: -moz-available;
	table-layout: fixed;
	cursor: pointer;
	&:hover {
		background: #f0f0f0;
	}
	@media (max-width: 768px) {
		padding: 4px 0px;
		border-bottom: 1px solid ${ColorV2.border.neutralSoft};
	}
`;

const StyledThead = styled.thead`
	position: sticky;
	top: 0;
	background-color: ${ColorV2.surface.invert};
	border-bottom: 1px solid ${ColorV2.border.neutralSoft};
	z-index: 1;
	display: table;
	width: 100%;
	table-layout: fixed;
`;

const StyledTr = styled.tr`
	height: 36px;
	padding: 8px 0px;
`;

const StyledTd = styled.td`
	padding-left: 8px;
`;

type TableColumn = {
	key: string;
	header: string;
	marginRight?: string;
	flexSize?: number;
	render?: (item: any) => React.ReactNode;
	icon?: string;
	onClick?: () => void;
};

interface Props {
	columns: TableColumn[];
	count?: number;
	secondaryCount?: number;
	secondaryTitle?: string;
	page?: number;
	data: any[];
	onRowClick: (rowData: any) => void;
	noResultsMessage?: string;
	showAvatar?: boolean;
	filters: Array<{
		style?: CSSProperties;
		id: string;
		placeholder: string;
		disabled?: boolean;
		selected?: boolean;
		onChange?: (active: boolean) => void;
		type?: "boolean" | "single" | "multiple" | "ratio" | "date";
		options?: Array<{
			id: string;
			label: string;
			sublabel?: string;
		}>;
		selectedOptions?: Array<string>;
		hideSearchBar?: boolean;
		position?: "bottom-right" | "bottom-left";
	}>;
}

const FiltersContainer = styled.div`
	display: flex;
	gap: 12px;
	align-items: center;
	margin-bottom: 8px;
	margin-top: 8px;
`;

const ToggleFilterButton = styled.button<{ $active?: boolean }>`
	padding: 12px 6px;
	border-radius: 1000px;

	border: ${({ $active }) =>
		$active
			? `2px solid ${ColorV2.surface.primary}`
			: `1px solid ${ColorV2.border.neutralSoft}`};

	background: ${({ $active }) =>
		$active ? ColorV2.surface.primarySoft : "white"};
	color: ${ColorV2.text.neutralHard};
	cursor: pointer;
	font-size: 14px;
	font-weight: 500;
	transition: 0.2s ease all;
	min-width: 102px;
	max-width: 102px;
	&:hover {
		opacity: 0.85;
	}
	@media (min-width: 768px) {
		margin-left: 20px;
	}
`;

const FilterButton = ({
	filter,
	onChange,
}: {
	filter: any;
	onChange?: (active: boolean) => void;
}) => {
	const selected = filter.selected ?? false;

	const toggle = () => {
		onChange?.(!selected);
	};

	return (
		<ToggleFilterButton $active={selected} onClick={toggle}>
			{filter.placeholder}
		</ToggleFilterButton>
	);
};

const StandardTable: React.FC<Props> = (props) => {
	const { showAvatar = true, ...restProps } = props;

	return (
		<>
			{props.filters?.length > 0 && (
				<FiltersContainer>
					{props.filters.map((filter) => {
						if (filter.type === "boolean") {
							return (
								<FilterButton
									key={filter.id}
									filter={filter}
									onChange={(active) =>
										filter.onChange?.(active)
									}
								/>
							);
						}
					})}
				</FiltersContainer>
			)}

			<StyledTable>
				{restProps.data.length > 0 &&
					restProps.columns.findIndex((item) => item.header) >= 0 && (
						<StyledThead>
							<StyledTr>
								{showAvatar && (
									<StyledTdAvatar></StyledTdAvatar>
								)}
								{restProps.columns.map((column, colIndex) => (
									<StyledTd
										key={colIndex}
										style={{
											width:
												column.key === "Nombre"
													? "50%"
													: "unset",
										}}
									>
										<HeaderCell
											$isAge={column.key === "Edad"}
										>
											<Text
												type="c1"
												weight="regular"
												style={{
													color: ColorV2.text
														.neutralMedium,
												}}
											>
												{column.header}
											</Text>

											{column.icon && (
												<img
													src={column.icon}
													alt="arrowUp"
													height={16}
													width={16}
													style={{
														marginLeft: "8px",
														cursor: "pointer",
													}}
													onClick={(e) => {
														e.stopPropagation();
														column.onClick?.();
													}}
												/>
											)}
										</HeaderCell>
									</StyledTd>
								))}
							</StyledTr>
						</StyledThead>
					)}

				<StyledTbody>
					{restProps.data.length > 0 ? (
						restProps.data.map((item, index) => (
							<ScrollableTr
								key={index}
								onClick={() => restProps.onRowClick(item)}
							>
								{showAvatar && (
									<StyledTdAvatar>
										<AvatarContainer>
											<Avatar
												name={item.name}
												icon={item.image?.url}
												style={{
													height: 40,
													width: 40,
													fontSize: 15,
													color: ColorV2.text.invert,
													objectFit: "cover",
													backgroundColor:
														ColorV2.surface
															.neutralMedium,
												}}
											/>
										</AvatarContainer>
									</StyledTdAvatar>
								)}
								{restProps.columns.map((column, colIndex) => (
									<StyledTd
										key={colIndex}
										style={{
											width:
												column.key === "Nombre"
													? "50%"
													: "unset",
										}}
									>
										{column.render
											? column.render(item)
											: item[column.key]}
									</StyledTd>
								))}
							</ScrollableTr>
						))
					) : (
						<ScrollableTr>
							<StyledTd
								colSpan={
									restProps.columns.length +
									(showAvatar ? 1 : 0)
								}
							>
								<Text
									type="h6"
									weight="regular"
									style={{
										textAlign: "center",
										marginTop: "40px",
										color: ColorV2.text.neutralMedium,
									}}
								>
									{restProps.noResultsMessage}
								</Text>
							</StyledTd>
						</ScrollableTr>
					)}
				</StyledTbody>
			</StyledTable>
		</>
	);
};

export default StandardTable;
